import type { BookingState } from '../state/types';
import type { TimeSlotMenu } from '../types/timeSlots';
import { PUBLIC_SEARCH_MODE } from '@shared/types/restaurant';
import { filterSlotMenusByArea } from './areaUtils';
import { applySelectedMenuFilter } from './menuFilter';

/**
 * A slot's menus scoped the way the EXPERIENCE step sees them: by the selected
 * area (area mode only), then by the guest's committed menu filter.
 * `areaScoped` is the list before the menu filter.
 */
export function scopeSlotMenus(
  slotMenus: TimeSlotMenu[],
  isAreaFlow: boolean,
  selectedArea: BookingState['selectedArea'],
  selectedMenuFilter: BookingState['selectedMenuFilter'],
): { areaScoped: TimeSlotMenu[]; menus: TimeSlotMenu[] } {
  const areaScoped = isAreaFlow && selectedArea
    ? filterSlotMenusByArea(slotMenus, selectedArea)
    : slotMenus;
  return { areaScoped, menus: applySelectedMenuFilter(areaScoped, selectedMenuFilter).menus };
}

/**
 * The slot menu a booking is priced against: the selected experience (looked
 * up across the whole slot, since that id is the menu the booking commits),
 * else the first enabled scoped menu.
 */
export function resolveActiveSlotMenu(
  slotMenus: readonly TimeSlotMenu[],
  scopedMenus: readonly TimeSlotMenu[],
  selectedExperienceId: string | undefined,
): TimeSlotMenu | undefined {
  return slotMenus.find((m) => String(m.id) === selectedExperienceId)
    ?? scopedMenus.find((m) => !m.disabled);
}

/** The active slot menu for a booking, via scopeSlotMenus + resolveActiveSlotMenu. */
export function activeSlotMenuFor(
  booking: Pick<BookingState, 'selectedTime' | 'timeSlots' | 'selectedArea' | 'selectedMenuFilter' | 'selectedExperience'>,
  publicSearchMode: string | undefined,
): TimeSlotMenu | undefined {
  const slot = booking.selectedTime && booking.timeSlots
    ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
    : null;
  if (!slot) return undefined;
  const slotMenus = slot.menus ?? [];
  const { menus } = scopeSlotMenus(
    slotMenus,
    publicSearchMode === PUBLIC_SEARCH_MODE.AREA,
    booking.selectedArea,
    booking.selectedMenuFilter,
  );
  return resolveActiveSlotMenu(slotMenus, menus, booking.selectedExperience?.id);
}
